import type { FC } from 'react'
import { useMemo } from 'react'
import { avgScore, daysAgo, scoreColor, type Bet } from '../domain/Bet.domain'
import { DashCard } from './DashCard'
import { ScoreIndicator } from './ScoreIndicator'

interface Props {
  bets: Bet[]
}

export const DashboardView: FC<Props> = ({ bets }) => {
  const stats = useMemo(() => {
    const allDelivered = bets.flatMap((b) => b.delivered)
    const del60 = allDelivered.filter((p) => p.date && daysAgo(p.date, 60))
    const del30 = allDelivered.filter((p) => p.date && daysAgo(p.date, 30))
    const activeIP = bets.filter((b) => b.inProgress.length > 0)

    const trends = bets.map((b) => {
      const allPts = [...b.delivered, ...b.inProgress, ...b.upNext]
      const recent = b.delivered.filter((p) => p.date && daysAgo(p.date, 30))
      const older = b.delivered.filter((p) => p.date && !daysAgo(p.date, 30))
      const recentAvg = recent.length ? avgScore(recent) : null
      const olderAvg = older.length ? avgScore(older) : null
      let trend: 'up' | 'down' | 'stable' = 'stable'
      if (recentAvg !== null && olderAvg !== null) {
        if (recentAvg > olderAvg + 0.3) trend = 'up'
        else if (recentAvg < olderAvg - 0.3) trend = 'down'
      }
      return {
        ...b,
        avg: allPts.length ? avgScore(allPts) : 0,
        trend,
        allPts,
      }
    })

    return {
      activeIP,
      del60,
      del30,
      growing: trends.filter((t) => t.trend === 'up'),
      declining: trends.filter((t) => t.trend === 'down'),
      trends,
    }
  }, [bets])

  return (
    <div>
      <div
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}
      >
        <DashCard
          label="Active / In Progress"
          value={stats.activeIP.length}
          color="#6366f1"
          sub={`of ${bets.length} total bets`}
          items={stats.activeIP.map((b) => ({
            thesis: b.thesis,
            avg: avgScore([...b.delivered, ...b.inProgress, ...b.upNext]),
          }))}
        />
        <DashCard
          label="Delivered (60d)"
          value={stats.del60.length}
          color="#10b981"
          sub="Last 2 months"
          items={stats.del60.map((p) => ({
            text: p.text,
            date: p.date,
            score: p.score,
          }))}
        />
        <DashCard
          label="Delivered (30d)"
          value={stats.del30.length}
          color="#059669"
          sub="Last month"
          items={stats.del30.map((p) => ({
            text: p.text,
            date: p.date,
            score: p.score,
          }))}
        />
      </div>

      <div
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}
      >
        <DashCard
          label="Growing Conviction"
          value={stats.growing.length}
          color="#10b981"
          sub="Score trending up"
          items={stats.growing.map((b) => ({
            thesis: b.thesis,
            trend: 'up' as const,
            avg: b.avg,
          }))}
        />
        <DashCard
          label="Declining Conviction"
          value={stats.declining.length}
          color="#ef4444"
          sub="Score trending down"
          items={stats.declining.map((b) => ({
            thesis: b.thesis,
            trend: 'down' as const,
            avg: b.avg,
          }))}
        />
      </div>

      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#374151',
          marginBottom: 10,
        }}
      >
        All Bets — Conviction Overview
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {stats.trends.map((b) => (
          <div
            key={b.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
            }}
          >
            <ScoreIndicator score={b.avg} size={20} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{b.thesis}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                {b.owner.name} · {b.allPts.length} proof points · avg{' '}
                {b.avg.toFixed(1)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {b.allPts.map((p, i) => (
                <div
                  key={i}
                  title={`${p.text} (${p.score})`}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: scoreColor(p.score),
                  }}
                />
              ))}
            </div>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color:
                  b.trend === 'up'
                    ? '#10b981'
                    : b.trend === 'down'
                      ? '#ef4444'
                      : '#9ca3af',
                width: 20,
                textAlign: 'center',
              }}
            >
              {b.trend === 'up' ? '↑' : b.trend === 'down' ? '↓' : '–'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
